import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { useDrag, useDrop } from 'react-dnd'
import Icon from '../../../atoms/Icon'
import { countFilterEntries } from '../lib/scopeFilterCompile'

const DRAG_TYPE = 'GROUPING_ITEM'

/**
 * Blade 2 row in the Groupings & Filters dialog. Extends the prior
 * CustomGroupingDialog row with:
 *   - click-to-focus (sets which grouping the detail panel renders for)
 *   - filter-count badge derived from the grouping's filter tree
 *
 * Drag is on the dedicated drag handle so a row click selects the row
 * for editing instead of accidentally starting a drag.
 */
export default function DraggableGroupingRow ({
  grouping,
  displayLabel,
  index,
  isSelected,
  filter,
  moveItem,
  onFocus,
  onRemove,
  classes,
  showFilterBadge = true
}) {
  const label = displayLabel || grouping
  const ref = useRef(null)

  const [{ isDragging }, drag] = useDrag({
    type: DRAG_TYPE,
    item: () => ({ index }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() })
  })

  const [, drop] = useDrop({
    accept: DRAG_TYPE,
    hover (item, monitor) {
      if (!ref.current) return
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return

      const hoverBoundingRect = ref.current.getBoundingClientRect()
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      const hoverClientY = clientOffset.y - hoverBoundingRect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return

      moveItem(dragIndex, hoverIndex)
      item.index = hoverIndex
    }
  })

  drop(ref)

  const filterCount = countFilterEntries(filter)

  return (
    <div
      ref={ref}
      className={clsx(classes.groupingRow, {
        [classes.groupingRowSelected]: isSelected,
        [classes.groupingRowDragging]: isDragging
      })}
      onClick={() => onFocus(grouping)}
      role='button'
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onFocus(grouping)
        }
      }}
    >
      <span ref={drag} className={classes.dragHandle} onClick={(e) => e.stopPropagation()}>
        <Icon name='dragIndicator' customSize={14} />
      </span>
      <span className={classes.depthIndicator}>{index + 1}.</span>
      <span className={classes.groupingLabel}>{label}</span>
      {showFilterBadge && (filterCount > 0
        ? <span className={classes.filterBadge}>{filterCount} filter{filterCount === 1 ? '' : 's'}</span>
        : <span className={classes.filterBadgeEmpty}>no filters</span>)}
      <span
        className={classes.removeBtn}
        onClick={(e) => {
          e.stopPropagation()
          onRemove(grouping)
        }}
        role='button'
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            e.stopPropagation()
            onRemove(grouping)
          }
        }}
        aria-label={`Remove ${label}`}
      >
        <Icon name='close' customSize={14} />
      </span>
    </div>
  )
}

DraggableGroupingRow.propTypes = {
  grouping: PropTypes.string.isRequired,
  displayLabel: PropTypes.string,
  index: PropTypes.number.isRequired,
  isSelected: PropTypes.bool,
  filter: PropTypes.object,
  moveItem: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  showFilterBadge: PropTypes.bool
}
